import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import ProductDemo from "@/components/ProductDemo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, BarChart3, Zap, Database, CheckCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import voiceAIHero from "@/assets/voice-ai-platform-hero.jpg";
import analyticsHero from "@/assets/analytics-suite-hero.jpg";
import outboundHero from "@/assets/outbound-automation-hero.jpg";
import crmIntegrationImage from "@/assets/crm-integration-hub.jpg";

const Products = () => {
  const products = [
    {
      icon: Mic,
      name: "Holionix Voice AI Platform",
      image: voiceAIHero,
      description: "Our flagship product—an enterprise-grade platform for deploying AI voice agents across inbound and outbound scenarios with unlimited scalability.",
      features: [
        "Natural language understanding with 20+ AI frameworks",
        "Multi-language & dialect support (10+ accents)",
        "Real-time sentiment analysis",
        "Context-aware multi-turn conversations",
        "Intelligent human handoff with full context transfer",
        "Omnichannel (Voice, SMS, WhatsApp, Webchat)",
      ],
      audience: "Call centers, customer support teams, sales organizations, BPO providers",
      benefit: "Reduce operational costs by 40% while improving customer satisfaction and handling unlimited concurrent interactions.",
      metrics: ["40% Cost Reduction", "99.9% Uptime", "Zero Wait Times"],
      demoType: "voice" as const,
    },
    {
      icon: BarChart3,
      name: "Holionix Analytics Suite",
      image: analyticsHero,
      description: "Real-time dashboards and comprehensive insights into every conversation, performance metrics, business outcomes, and ROI tracking.",
      features: [
        "Live conversation monitoring & transcription",
        "Sentiment timeline tracking",
        "Intent classification breakdown",
        "Performance KPIs & SLA monitoring",
        "ROI calculators & cost analysis",
        "Customizable reports & data exports",
        "BI tool integration (Tableau, Power BI, Looker)",
      ],
      audience: "Operations managers, C-suite executives, data analysts, quality assurance teams",
      benefit: "Make data-driven decisions with actionable insights from every customer interaction. Identify trends, optimize workflows, and prove ROI.",
      metrics: ["Real-Time Insights", "Custom Reports", "Full API Access"],
      demoType: "analytics" as const,
    },
    {
      icon: Zap,
      name: "Holionix Outbound Automation Suite",
      image: outboundHero,
      description: "Specialized solution for scaling outbound sales, marketing campaigns, customer outreach, and appointment setting with AI voice agents.",
      features: [
        "Automated lead qualification & scoring",
        "Intelligent appointment scheduling",
        "Campaign automation with A/B testing",
        "CRM integration & auto-updates",
        "Dynamic script optimization",
        "Personalized messaging at scale",
      ],
      audience: "Sales teams, marketing departments, growth-focused organizations, lead generation agencies",
      benefit: "3x more qualified leads with 60% lower cost per acquisition. Consistent messaging, unlimited scale, and complete tracking.",
      metrics: ["3x More Leads", "-60% Cost/Lead", "Unlimited Outreach"],
      demoType: "outbound" as const,
    },
    {
      icon: Database,
      name: "Holionix Integration Hub",
      image: crmIntegrationImage,
      description: "Zero-code integration layer that connects seamlessly with your existing CRM, IVR, cloud infrastructure, and business tools.",
      features: [
        "Pre-built connectors for 50+ platforms",
        "Salesforce, HubSpot, SAP, Zendesk, Microsoft Dynamics",
        "Custom API integrations",
        "Real-time data synchronization",
        "Webhook support",
        "24-hour deployment timeline",
      ],
      audience: "IT departments, operations teams, system integrators",
      benefit: "Deploy in hours, not months. No coding required. Maintain your existing workflows while adding AI capabilities.",
      metrics: ["Zero-Code Setup", "24hr Deployment", "50+ Integrations"],
      demoType: "integration" as const,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 gradient-brand-text tracking-tight">Our Products</h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Enterprise AI voice solutions built for scale, compliance, and seamless integration with your workflows
            </p>
          </motion.div>

          <div className="space-y-32 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                className="space-y-12"
              >
                <div className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  {/* Image Side */}
                  <div className={`${index % 2 === 1 ? 'md:col-start-1' : ''}`}>
                    <Card className="overflow-hidden glass-effect border-primary/40 hover-lift group">
                      <div className="relative h-[32rem] overflow-hidden">
                        <motion.img 
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        <div className="absolute top-8 right-8">
                          <div className="px-5 py-2 rounded-full gradient-brand text-white text-sm font-bold flex items-center gap-2 shadow-2xl">
                            <Sparkles className="w-4 h-4" />
                            Featured Product
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Content Side */}
                  <div className={`${index % 2 === 1 ? 'md:col-start-2 md:row-start-1' : ''} space-y-8`}>
                    <div>
                      <div className="flex items-center gap-5 mb-6">
                        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-2xl glow-effect">
                          <product.icon className="w-12 h-12 text-white" />
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{product.name}</h2>
                      </div>
                      <p className="text-xl text-muted-foreground leading-relaxed mb-8">{product.description}</p>
                    </div>
                    
                    <Card className="p-8 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
                      <h3 className="font-bold mb-5 flex items-center gap-3 text-xl">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                        Key Features
                      </h3>
                      <ul className="space-y-3">
                        {product.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-muted-foreground text-lg">
                            <div className="w-2.5 h-2.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                    
                    <div className="flex flex-wrap gap-4">
                      {product.metrics.map((metric, idx) => (
                        <motion.span
                          key={idx}
                          initial={{ scale: 0.9, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                          className="px-8 py-4 rounded-xl bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/30 font-bold text-lg"
                        >
                          {metric}
                        </motion.span>
                      ))}
                    </div>
                    
                    <Card className="p-8 glass-effect border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                      <div className="flex items-start gap-4">
                        <CheckCircle className="w-7 h-7 text-green-400 flex-shrink-0 mt-1" />
                        <p className="text-muted-foreground font-semibold text-lg leading-relaxed">{product.benefit}</p>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Interactive Demo Section */}
                <div className="mt-16">
                  <ProductDemo productType={product.demoType} />
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <Card className="p-12 glass-effect border-primary/20 bg-gradient-to-br from-primary/10 to-accent/10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Need a Custom Solution?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                We can tailor our products to your specific business requirements. Let's discuss your unique needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="gradient-brand text-white hover:opacity-90">
                    Talk to Sales
                  </Button>
                </Link>
                <Link to="/analytics">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    Explore Analytics
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Products;